import type {
	AgentSession,
	Artifact,
	ChatMessage,
	GitRepository,
	Project,
	WorkflowExecution,
	WorkOrder,
	WorkOrderEvidence,
	WorkOrderQuestion,
	WorkOrderRecord,
	WorkOrderSession,
} from "@agentry/zerovia-contracts";
import type {
	ApprovalRequest,
	Budget,
	ByoPolicy,
	CatalogPublication,
	ContactMessage,
	Integration,
	Notice,
	NotificationItem,
	OwnershipTransferRecord,
	PersonalCredential,
	Plan,
	PoolAssignment,
	ProductSettings,
	TenantCatalogEntitlement,
	TenantInvite,
	TenantMember,
	TenantSettings,
	UserAttribute,
	WorkspaceEntry,
} from "../agentry-types.js";
import type {
	AuditEventView,
	CostEventView,
	ProjectRepositoryConnection,
	WorkOrderFrameView,
	WorkOrderProgressView,
} from "../types.js";
import * as boardSeed from "./board-seed.js";
import * as seed from "./seed.js";

/** Mutableな部分だけを持つMock State。Catalog等の不変DataはSeedを直接参照する。 */
export type MockState = {
	version: 2;
	projects: Project[];
	repositories: GitRepository[];
	projectRepositoryConnections: ProjectRepositoryConnection[];
	sessions: AgentSession[];
	messages: ChatMessage[];
	executions: WorkflowExecution[];
	artifacts: Artifact[];
	artifactContents: Record<string, string>;
	approvals: ApprovalRequest[];
	workspaceEntries: WorkspaceEntry[];
	ownershipTransfers: OwnershipTransferRecord[];
	personalCredentials: PersonalCredential[];
	poolAssignments: PoolAssignment[];
	byoPolicy: ByoPolicy;
	members: TenantMember[];
	invites: TenantInvite[];
	catalogEntitlements: TenantCatalogEntitlement[];
	budgets: Budget[];
	integrations: Integration[];
	tenantSettings: TenantSettings;
	publications: CatalogPublication[];
	plans: Plan[];
	notices: Notice[];
	contactMessages: ContactMessage[];
	productSettings: ProductSettings;
	costEvents: CostEventView[];
	auditEvents: AuditEventView[];
	notifications: NotificationItem[];
	userAttributes: Array<UserAttribute & { userId: string }>;
	workOrders: WorkOrder[];
	workOrderQuestions: WorkOrderQuestion[];
	workOrderRecords: WorkOrderRecord[];
	workOrderEvidence: WorkOrderEvidence[];
	workOrderSessions: WorkOrderSession[];
	workOrderFrames: WorkOrderFrameView[];
	workOrderProgress: Record<string, WorkOrderProgressView>;
	/** Idempotency-Key -> 送信Bodyのhashと結果。Replay/Mismatch判定に使う。 */
	idempotency: Record<string, { bodyHash: string; resultRef: string }>;
	/** 単調増加のID採番。決定的にするためTimestampを使わない。 */
	sequence: number;
};

export function initialState(): MockState {
	const state = buildState();
	// Seedの固定Checksumではなく本文から導出した値を正本にする。
	// これによりArtifact取得時のChecksum再検証がMockでも意味を持つ。
	for (const artifact of state.artifacts) {
		const content = state.artifactContents[artifact.id];
		if (content === undefined) continue;
		artifact.checksum = checksumOf(content);
		artifact.sizeBytes = content.length;
	}
	return state;
}

function buildState(): MockState {
	return {
		version: 2,
		projects: clone(seed.projects),
		repositories: clone(seed.repositories),
		projectRepositoryConnections: clone(seed.projectRepositoryConnections),
		sessions: clone(seed.sessions),
		messages: clone(seed.messages),
		executions: clone(seed.executions),
		artifacts: clone(seed.artifacts),
		artifactContents: clone(seed.artifactContents),
		approvals: clone(seed.approvals),
		workspaceEntries: clone(seed.workspaceEntries),
		ownershipTransfers: clone(seed.ownershipTransfers),
		personalCredentials: clone(seed.personalCredentials),
		poolAssignments: clone(seed.poolAssignments),
		byoPolicy: clone(seed.byoPolicy),
		members: clone(seed.members),
		invites: clone(seed.invites),
		catalogEntitlements: clone(seed.catalogEntitlements),
		budgets: clone(seed.budgets),
		integrations: clone(seed.integrations),
		tenantSettings: clone(seed.tenantSettings),
		publications: clone(seed.publications),
		plans: clone(seed.plans),
		notices: clone(seed.notices),
		contactMessages: clone(seed.contactMessages),
		productSettings: clone(seed.productSettings),
		costEvents: clone(seed.costEvents),
		auditEvents: clone(seed.auditEvents),
		notifications: clone(seed.notifications),
		userAttributes: [],
		workOrders: clone(boardSeed.workOrders),
		workOrderQuestions: clone(boardSeed.questions),
		workOrderRecords: clone(boardSeed.records),
		workOrderEvidence: clone(boardSeed.evidence),
		workOrderSessions: clone(boardSeed.sessions),
		workOrderFrames: clone(boardSeed.frames),
		workOrderProgress: clone(boardSeed.progress),
		idempotency: {},
		sequence: 1000,
	};
}

function clone<T>(value: T): T {
	return JSON.parse(JSON.stringify(value)) as T;
}

export interface StatePersistence {
	read(): MockState | null;
	write(state: MockState): void;
	clear(): void;
}

/** Testやサーバー描画で使うメモリ実装。 */
export class MemoryPersistence implements StatePersistence {
	private value: MockState | null = null;
	read() {
		return this.value;
	}
	write(state: MockState) {
		this.value = state;
	}
	clear() {
		this.value = null;
	}
}

/**
 * Browser用。再読込・再Login後もArtifactを再取得できることを検証するために永続化する。
 * localStorageが使えない環境では黙って壊れず、メモリへ退避する。
 */
export class LocalStoragePersistence implements StatePersistence {
	private fallback = new MemoryPersistence();

	constructor(private readonly key = "agentry.mock.state.v2") {}

	private get storage(): Storage | null {
		try {
			if (typeof localStorage === "undefined") return null;
			return localStorage;
		} catch {
			return null;
		}
	}

	read(): MockState | null {
		const storage = this.storage;
		if (!storage) return this.fallback.read();
		const raw = storage.getItem(this.key);
		if (!raw) return null;
		try {
			const parsed = JSON.parse(raw) as MockState;
			return parsed.version === 2 ? parsed : null;
		} catch {
			return null;
		}
	}

	write(state: MockState): void {
		const storage = this.storage;
		if (!storage) {
			this.fallback.write(state);
			return;
		}
		try {
			storage.setItem(this.key, JSON.stringify(state));
		} catch {
			this.fallback.write(state);
		}
	}

	clear(): void {
		this.storage?.removeItem(this.key);
		this.fallback.clear();
	}
}

export class MockStore {
	private state: MockState;

	constructor(private readonly persistence: StatePersistence) {
		this.state = persistence.read() ?? initialState();
		this.state.projects ??= clone(seed.projects);
		this.state.repositories ??= clone(seed.repositories);
		this.state.projectRepositoryConnections ??= clone(seed.projectRepositoryConnections);
		this.state.userAttributes ??= [];
	}

	read(): MockState {
		return this.state;
	}

	mutate<T>(fn: (state: MockState) => T): T {
		const result = fn(this.state);
		this.persistence.write(this.state);
		return result;
	}

	reset(): void {
		this.state = initialState();
		this.persistence.write(this.state);
	}

	nextId(prefix: string): string {
		return this.mutate((state) => {
			state.sequence += 1;
			return `${prefix}-${state.sequence}`;
		});
	}
}

/** 決定的なhash。Idempotency Bodyの一致判定に使う（暗号用途ではない）。 */
export function stableHash(value: unknown): string {
	const text = JSON.stringify(value ?? null);
	let h1 = 0x811c9dc5;
	let h2 = 0x01000193;
	for (let i = 0; i < text.length; i += 1) {
		const c = text.charCodeAt(i);
		h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
		h2 = Math.imul(h2 + c, 0x85ebca6b) >>> 0;
	}
	return `${h1.toString(16).padStart(8, "0")}${h2.toString(16).padStart(8, "0")}`;
}

/** Artifact本文からChecksumを導出する。Mock/Realで同じ検証UIを使えるようにする。 */
export function checksumOf(content: string): string {
	let acc = 0x811c9dc5;
	for (let i = 0; i < content.length; i += 1) {
		acc = Math.imul(acc ^ content.charCodeAt(i), 0x01000193) >>> 0;
	}
	const base = acc.toString(16).padStart(8, "0");
	return `sha256:${base.repeat(8).slice(0, 64)}`;
}
